import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, FormControl, Grid, MenuItem, Select, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { sanitizeUTF8 } from 'helpers';
import {
  EstateResultItemType,
  EstateResultSource,
  useEstateResultItem_CreateResultItemMutation,
  useEstateResultItem_GetEstateResultItemByIdLazyQuery,
  useEstateResultItem_UpdateEstateResultItemMutation,
} from 'queries';

import { EstateResultsPageContext } from 'pages/estate-results-page/context/estate-results-page-context';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

import AddAccountItem from './add-account-item';
import AddAddressItem from './add-address-item';
import AddBankruptcyItem from './add-bankruptcy-item';
import AddCollectionItem from './add-collection-item';
import AddEmploymentItem from './add-employment-item';
import AddNameItem from './add-name-item';
import AddPropertyItem from './add-property-item';
import AddTradeItem from './add-trade-item';

type Props = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  onExitClick: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  refetchResults?: Function;
};

const useStyles = makeStyles(() => ({
  title: {
    paddingTop: '5px',
  },
  container: {
    paddingTop: '10px',
  },
  form: {
    textAlign: 'left',
  },
  body: {
    textAlign: 'center',
    width: '100%',
  },
  select: {
    width: '100%',
  },
}));

export default function CreateOrEditResultItemForm({ onExitClick, refetchResults }: Props) {
  const classes = useStyles();
  const context = useContext(EstateResultsPageContext);
  const { portalId, estateId } = useParams<Record<string, string>>();
  const [createEstateResultItem, { loading: createEstateResultItemExecuting }] =
    useEstateResultItem_CreateResultItemMutation({
      onError: (e) => {
        showAlert({
          message: 'Error creating result item: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const [getEstateResultItemById] = useEstateResultItem_GetEstateResultItemByIdLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (data.data) {
        determineFormType(data.data.type, data.data);
      }
    },
    onError: (e) => {
      showAlert({
        message: 'Error retrieving result item: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [updateExistingEstateResultItem] = useEstateResultItem_UpdateEstateResultItemMutation({
    onCompleted: (data) => {
      if (data.data) {
        showToast({
          message: 'Estate result item updated.',
          severity: NotificationSeverity.SUCCESS,
        });
        onExitClick();
      }
    },
    onError: (e) => {
      showAlert({
        message: 'Error updating result item: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [itemType, setItemType] = useState(EstateResultItemType.Account);
  const [formType, setFormType] = useState<JSX.Element>();
  const [essentialData, setEssentialData] = useState(Object);

  const determineFormType = (type: EstateResultItemType, data?: any) => {
    switch (type) {
      case EstateResultItemType.Account:
        setFormType(<AddAccountItem setEssentialData={setEssentialData} estateResultItem={data} />);
        break;
      case EstateResultItemType.Address:
        setFormType(<AddAddressItem setEssentialData={setEssentialData} />);
        break;
      case EstateResultItemType.Bankruptcy:
        setFormType(
          <AddBankruptcyItem setEssentialData={setEssentialData} estateResultItem={data} />,
        );
        break;
      case EstateResultItemType.Collection:
        setFormType(
          <AddCollectionItem setEssentialData={setEssentialData} estateResultItem={data} />,
        );
        break;
      case EstateResultItemType.Employment:
        setFormType(<AddEmploymentItem setEssentialData={setEssentialData} />);
        break;
      case EstateResultItemType.Name:
        setFormType(<AddNameItem setEssentialData={setEssentialData} />);
        break;
      case EstateResultItemType.Property:
        setFormType(
          <AddPropertyItem setEssentialData={setEssentialData} estateResultItem={data} />,
        );
        break;
      case EstateResultItemType.Trade:
        setFormType(<AddTradeItem setEssentialData={setEssentialData} estateResultItem={data} />);
        break;
    }
    setItemType(type);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelectChange = (e: any) => {
    // clear the essentialData for each selection
    setEssentialData({});
    if (e.target.value) {
      determineFormType(e.target.value);
    }
  };

  const handleSubmit = () => {
    if (context.isEditingEstateResultItem) {
      updateExistingEstateResultItem({
        variables: {
          input: {
            id: `${context.estateResultItemIdToEdit}`,
            portalId: portalId,
            source: EstateResultSource.Attorney,
            type: itemType,
            essentialData: JSON.stringify(essentialData, sanitizeUTF8),
          },
        },
      });
      return;
    }
    createNewEstateResultItem();
  };

  const createNewEstateResultItem = () => {
    createEstateResultItem({
      variables: {
        input: {
          id: estateId,
          portalId: portalId,
          type: itemType,
          essentialData: JSON.stringify(essentialData, sanitizeUTF8),
        },
      },
      onCompleted: () => {
        showToast({
          message: 'A new item has been created.',
          severity: NotificationSeverity.SUCCESS,
        });
        if (refetchResults) {
          refetchResults();
        }
        onExitClick();
      },
      onError: () => {
        showAlert({
          message: 'An error has occurred while saving the item.',
          severity: NotificationSeverity.ERROR,
        });
        //? Bad UX to auto-close during an error?
        // onExitClick();
      },
    });
  };

  useEffect(() => {
    if (!formType) {
      setFormType(<AddAccountItem setEssentialData={setEssentialData} />);
    }
  }, [formType]);

  useEffect(() => {
    if (context.isEditingEstateResultItem && context.estateResultItemIdToEdit) {
      getEstateResultItemById({
        variables: { id: context.estateResultItemIdToEdit },
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context]);

  return (
    <>
      <Typography variant="h5" className={classes.title}>
        {
          //? "Result Item" is not the most user friendly terminology
        }
        {context.isEditingEstateResultItem ? 'Edit Result Item' : 'Create Result Item'}
      </Typography>
      <form id="create-result-item-form" className={classes.form} onSubmit={handleSubmit}>
        <Grid container className={classes.container}>
          <Grid className={classes.select}>
            <FormControl fullWidth>
              <Typography variant="caption">Result Item Type</Typography>
              <Select
                fullWidth
                variant="outlined"
                name="resultItemType"
                value={itemType}
                disabled={context.isEditingEstateResultItem}
                onChange={handleSelectChange}
              >
                {Object.keys(EstateResultItemType).map((key) => {
                  if (
                    !key.includes('Form') &&
                    !key.includes('Source') &&
                    !key.includes('Document')
                  ) {
                    return (
                      <MenuItem
                        key={key}
                        value={EstateResultItemType[key as keyof typeof EstateResultItemType]}
                      >
                        <Typography>{key}</Typography>
                      </MenuItem>
                    );
                  }
                  return '';
                })}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container className={classes.container}>
          <Grid className={classes.body}>{formType}</Grid>
        </Grid>
        <Grid container className={classes.container}>
          <Grid>
            <Button
              type="submit"
              form="coupon-form"
              color="secondary"
              variant="contained"
              disabled={createEstateResultItemExecuting}
              onClick={handleSubmit}
            >
              Save
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
}
